import React from 'react';
import { SEJOUR_EMPTY } from "./SecProp.jsx";
import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { EVIIVO_HEURES, CTRL_CHECKS, CTRL_PIECES, SUPA_URL, SUPA_KEY} from "../constants.js";
import { fd, feur, compressToDataUrl, dataUrlSizeKB, fname, ft, now, today, appliquerMarquageControle, detectPmsVendor, pmsLabel } from "../utils.js";
import { Ic } from "./Ic.jsx";
import { M } from "./M.jsx";
import { supaGet, supaSet, useSt, syncBus, supaAuthHeader, countBase64Photos, uploadDataUrlToStorage, getSignedFactureUrl, uploadPhotosToStorage, migrateAllPhotosToStorage, countBase64PhotosCtrl, uploadCtrlPieceArray, migrateAllPhotosToStorageCtrl } from "../supabase.js";
import { realtimeBus, startRealtimeChannel, suspendRealtime, resumeRealtime } from "../realtime.js";
import { enqueueControle } from "../localQueue.js";
import { generateRapportPdf, generateControlePdf } from "../pdf-utils.js";
import { ErrorBoundary, RapportModal, getRapportStructure } from "./ErrorBoundary.jsx";
import { fusionnerRapportAdmin } from "../rapportAdminEdit.js";

// ─── File locale : attente BORNÉE de l'écriture disque (v133) ────────────────
// Incident du 04/08/2026 (contrôle Schwendi perdu) : `enqueueControle` était lancée
// SANS `await`, la modale se refermait aussitôt, et fermer l'app dans les secondes
// qui suivaient perdait le contrôle — l'écriture IndexedDB n'ayant jamais eu le
// temps d'aboutir. Le rapport de ménage, lui, attend son `enqueueRapport` depuis
// toujours (rapportCore.js) : c'est TOUTE la différence de fiabilité entre les deux.
//
// Le bornage est OBLIGATOIRE, pas décoratif : la règle de localQueue.js §8.1 dit
// qu'une file cassée ne doit JAMAIS bloquer le chemin réseau. Sans timeout, un
// IndexedDB qui ne répond pas figerait la modale sur « Enregistrement… »
// indéfiniment — pire que le bug d'origine. Au-delà du délai on poursuit sans la
// garantie locale, en le disant dans la console.
const FILE_LOCALE_TIMEOUT_MS=10000;
async function attendreFileLocale(promesse){
  try{
    const issue=await Promise.race([
      promesse,
      new Promise(res=>setTimeout(()=>res('__timeout__'),FILE_LOCALE_TIMEOUT_MS)),
    ]);
    if(issue==='__timeout__')console.error('[file locale] écriture contrôle non confirmée en',FILE_LOCALE_TIMEOUT_MS,'ms — on poursuit sans garantie locale');
  }catch(e){
    // enqueueControle avale déjà ses erreurs ; on ne laisse jamais rien remonter :
    // l'échec de la file ne doit pas empêcher l'enregistrement de se poursuivre.
    console.error('[file locale] écriture contrôle échouée (non bloquant)',e);
  }
}

// ── Impression groupée : contrôle + rapport de ménage associé (v151) ──
// Demande de Claire : ne plus avoir à rechercher le rapport de ménage séparément.
// Un clic, deux fichiers. Léger délai entre les deux `save()` : certains navigateurs
// bloquent/questionnent les téléchargements multiples déclenchés dans le même tick.
async function printControleEtMenage({controle,bien,rapport,worker,als,dureeAnom}){
  await generateControlePdf({controle,bien,rapport,worker,als,dureeAnom});
  if(rapport&&bien){
    const struct=getRapportStructure(bien);
    await new Promise(res=>setTimeout(res,400));
    await generateRapportPdf({rapport,bien,worker,struct,controle,dureeAnom});
  }
}

function NoteEtoiles({val,onChange}){
  const labels={1:'❌ Insuffisant',2:'⚠️ Passable',3:'✅ Bien',4:'⭐ Excellent'};
  // Dégradé intuitif rouge→orange→vert (Claire, 10/08) : avant, 3 était vert et 4 orange —
  // la meilleure note redevenait "moins bonne" visuellement. Désormais 1→4 va du rouge au
  // vert le plus soutenu, sans retour en arrière de teinte.
  const colors={1:'var(--dg)',2:'var(--or)',3:'var(--mn)',4:'var(--ok)'};
  const bgs={1:'#fde0e0',2:'#ffe9d6',3:'#eaf5ec',4:'#e1f7ee'};
  return(<div style={{display:'flex',flexDirection:'column',gap:5}}>
    <div style={{display:'flex',gap:6}}>
      {[1,2,3,4].map(n=>(<button key={n} onClick={()=>onChange(n)} style={{width:44,height:44,borderRadius:10,border:`2px solid ${val===n?colors[n]:'var(--li)'}`,fontSize:18,cursor:'pointer',fontWeight:700,background:val===n?bgs[n]:'var(--wh)',color:val===n?colors[n]:'var(--si)'}}>{n}</button>))}
    </div>
    {val&&<div style={{fontSize:11,color:colors[val],fontWeight:700}}>{labels[val]}</div>}
  </div>);
}

function CtrlModal({repId,bien,controleurs,existing,onSave,onClose,onDirtyChange,sess}){
  // ── v2 : pièces dynamiques depuis getRapportStructure ──
  const struct=useMemo(()=>getRapportStructure(bien),[bien]);
  const dynSections=struct.sections||[];
  const allRapportItems=struct.items||[];
  const isV2=!existing||existing.version===2;
  // Pièces : v2 = dynamiques (rm0, rm1…), v1 legacy = CTRL_PIECES (cuisine, salon…)
  const initPieces=Object.fromEntries(
    isV2?dynSections.map(s=>[s.key,{note:null,obs:'',photos:[]}])
        :CTRL_PIECES.map(p=>[p.id,{note:null,obs:'',photos:[]}])
  );
  const initChecks=Object.fromEntries(CTRL_CHECKS.map(c=>[c.id,null]));
  const[f,setF]=useState(existing||{id:'ctrl'+Date.now(),version:2,repId,bid:bien?.id,pieces:initPieces,checks:initChecks,items:{},verdict:null,notes:'',controlleurId:sess?.id||null,history:[]});
  const[step,setStep]=useState(0);
  // Suivi "modifications non sauvegardées" — aucune écriture Supabase n'existe entre l'ouverture
  // et le premier verdict/"Sauvegarder sans verdict" ; ce flag sert uniquement à demander confirmation
  // avant une fermeture accidentelle (backdrop, croix, "Annuler"). Snapshot pris une seule fois au montage.
  const initialRef=useRef(f);
  const isDirty=useMemo(()=>JSON.stringify(f)!==JSON.stringify(initialRef.current),[f]);
  useEffect(()=>{onDirtyChange&&onDirtyChange(isDirty);},[isDirty]);
  useEffect(()=>()=>{onDirtyChange&&onDirtyChange(false);},[]);
  const set=(k,v)=>setF(x=>({...x,[k]:v}));
  const setPiece=(pid,k,v)=>setF(x=>({...x,pieces:{...x.pieces,[pid]:{...x.pieces[pid],[k]:v}}}));
  const setCheck=(cid,v)=>setF(x=>({...x,checks:{...x.checks,[cid]:v}}));
  // ── v2 : items du rapport de ménage (sparse : seuls les problèmes sont stockés) ──
  const setItem=(itemId,k,v)=>setF(x=>{
    const cur=x.items||{};
    const entry={...(cur[itemId]||{ok:false}),[k]:v};
    return{...x,items:{...cur,[itemId]:entry}};
  });
  const toggleItem=(itemId)=>setF(x=>{
    const cur=x.items||{};
    if(cur[itemId]){const next={...cur};delete next[itemId];return{...x,items:next};}
    return{...x,items:{...cur,[itemId]:{ok:false}}};
  });
  // Compteur non-conformités (v2 only)
  const nonConf=useMemo(()=>Object.values(f.items||{}).filter(it=>it.ok===false).length,[f.items]);
  const totalItems=allRapportItems.length;
  // Score global : v2 = pièces dynamiques, v1 = CTRL_PIECES
  const scoreGlobal=useMemo(()=>{
    const keys=isV2?dynSections.map(s=>s.key):CTRL_PIECES.map(p=>p.id);
    const ns=keys.map(k=>f.pieces[k]?.note).filter(Boolean);
    return ns.length?(ns.reduce((s,n)=>s+n,0)/ns.length).toFixed(1):null;
  },[f.pieces,isV2,dynSections]);
  const scoreColor=scoreGlobal>=3.5?'var(--ok)':scoreGlobal>=2.5?'#7a5a00':scoreGlobal>=1.5?'var(--or)':'var(--dg)';
  // Chantier B (01/08/2026) : écriture locale AVANT le réseau, centralisée ICI plutôt
  // que dupliquée dans les 3 endroits qui utilisent CtrlModal (SecControles, StaffUI,
  // GerantUI) — ils en bénéficient tous automatiquement. Si le rejeu silencieux au
  // prochain démarrage doit reconstruire ce contrôle, queueAutoReplay.js s'en charge.
  //
  // v133 (Lot A) : l'écriture locale est désormais ATTENDUE avant de rendre la main,
  // et un état visible empêche de croire que c'est fini alors que ça ne l'est pas.
  // Même pattern que `saving` dans RapportModal (ErrorBoundary.jsx), déjà éprouvé.
  const[savingWhat,setSavingWhat]=useState(null); // null | 'pret' | 'nonpret' | 'reporter' | 'draft'
  const saving=savingWhat!==null;
  // Upload des photos de contrôle vers Storage — miroir de RapportModal/rep7 (session
  // 11/08/2026) : jamais fait pour les contrôles jusqu'ici, cause du gonflement sans limite
  // du document unique controles7 en base64 et des sauvegardes qui échouent/traînent.
  // Appelé APRÈS l'écriture locale (règle absolue localQueue §3 : rien ne part sur le réseau
  // avant d'être sur le disque). Best-effort, comme RapportModal : une photo qui échoue reste
  // en base64, la sauvegarde ne plante jamais pour autant.
  const uploadCtrlPhotos=async val=>{
    try{
      const pieces={...(val.pieces||{})};
      for(const pieceId of Object.keys(pieces)){
        const list=(pieces[pieceId]&&pieces[pieceId].photos)||[];
        if(!list.some(v=>typeof v==='string'&&v.startsWith('data:image')))continue;
        const recompressed=[];
        for(const v of list){
          if(typeof v==='string'&&v.startsWith('data:image')&&dataUrlSizeKB(v)>250){
            try{recompressed.push(await compressToDataUrl(v,{maxDim:1024,quality:0.6}));}
            catch(e){console.error('[ctrl photo] recompress échoué',pieceId,e);recompressed.push(v);}
          }else recompressed.push(v);
        }
        const uploaded=await uploadCtrlPieceArray(recompressed,val.id,pieceId);
        pieces[pieceId]={...pieces[pieceId],photos:uploaded};
      }
      // v2 : upload des photos d'items (non-conformités)
      const items={...(val.items||{})};
      for(const itemId of Object.keys(items)){
        const list=(items[itemId]&&items[itemId].photos)||[];
        if(!list.some(v=>typeof v==='string'&&v.startsWith('data:image')))continue;
        const recompressed=[];
        for(const v of list){
          if(typeof v==='string'&&v.startsWith('data:image')&&dataUrlSizeKB(v)>250){
            try{recompressed.push(await compressToDataUrl(v,{maxDim:1024,quality:0.6}));}
            catch(e){console.error('[ctrl item photo] recompress échoué',itemId,e);recompressed.push(v);}
          }else recompressed.push(v);
        }
        const uploaded=await uploadCtrlPieceArray(recompressed,val.id,'item_'+itemId);
        items[itemId]={...items[itemId],photos:uploaded};
      }
      return{...val,pieces,items};
    }catch(e){
      console.error('[ctrl photo] upload Storage échoué (non bloquant)',e);
      return val;
    }
  };
  const commitSave=async(val,close,what)=>{
    if(saving)return;
    setSavingWhat(what||'draft');
    await attendreFileLocale(enqueueControle({recordId:val.id,payload:val,sid:sess?.id,authorLabel:sess?.name||'',fin:close}));
    const valUploaded=await uploadCtrlPhotos(val);
    onSave(valUploaded,close);
    // close=true → les 3 appelants (SecControles, StaffUI, GerantUI) démontent la
    // modale : ne pas toucher au state ensuite. close=false → on reste affiché.
    if(!close)setSavingWhat(null);
  };
  // Enrichit un objet contrôle avec les champs v2 calculés avant sauvegarde
  const enrichV2=obj=>{
    if(!isV2)return obj;
    const nc=Object.values(obj.items||{}).filter(it=>it.ok===false).length;
    return{...obj,version:2,nonConformites:nc,totalItemsRapport:totalItems};
  };
  const doVerdict=v=>{
    const entry={action:v,by:sess?.name||sess?.id||'admin',at:now()};
    const saved=enrichV2({...f,verdict:v,closedAt:v!=='reporter'?now():null,history:[...(f.history||[]),entry]});
    commitSave(saved,true,v);
  };
  return(<div style={{display:'flex',flexDirection:'column',gap:0,maxHeight:'72vh',overflow:'hidden'}}>
    {/* Stepper */}
    <div style={{display:'flex',gap:0,marginBottom:14,borderRadius:10,overflow:'hidden',border:'1px solid var(--li)'}}>
      {['Pièces','Vérifications','Verdict'].map((l,i)=>(
        <button key={i} onClick={()=>setStep(i)} style={{flex:1,padding:'8px 4px',fontSize:12,fontWeight:700,cursor:'pointer',border:'none',background:step===i?'var(--ink)':'var(--wh)',color:step===i?'white':'var(--si)'}}>{step>i?'✓ ':''}{l}</button>
      ))}
    </div>
    <div style={{flex:1,overflowY:'auto',paddingRight:2}}>
      {/* PIÈCES */}
      {step===0&&<div style={{display:'flex',flexDirection:'column',gap:12}}>
        <div style={{fontSize:11,color:'var(--si)',fontStyle:'italic',padding:'8px 12px',background:'var(--cr)',borderRadius:8}}>🧹 Notez chaque pièce. {isV2?'Cochez les éléments du rapport de ménage qui ne sont PAS faits ou mal faits.':'Vous pouvez corriger les problèmes mineurs sur place avant de noter.'}</div>
        {(isV2?dynSections.map(s=>({id:s.key,icon:s.room?.emoji||'📦',label:s.room?.nom||s.title,_items:s.items})):CTRL_PIECES.map(p=>({...p,_items:[]}))).map(p=>(<div key={p.id} className="card" style={{padding:'14px',borderLeft:`3px solid ${f.pieces[p.id]?.note?'var(--mn)':'var(--li)'}`}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:10}}>{p.icon} {p.label}</div>
          <NoteEtoiles val={f.pieces[p.id]?.note} onChange={v=>setPiece(p.id,'note',v)}/>
          <div style={{marginTop:10}}><label className="fl">Observations</label><textarea rows="2" value={f.pieces[p.id]?.obs||''} onChange={e=>setPiece(p.id,'obs',e.target.value)} placeholder="Problème constaté, rideaux en vrac, poussière…" style={{fontSize:12}}/></div>
          <div style={{marginTop:8}}>
            <label className="fl">Photos</label>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {(f.pieces[p.id]?.photos||[]).map((ph,i)=>(<div key={i} style={{position:'relative'}}><img src={ph} style={{width:54,height:54,objectFit:'cover',borderRadius:8}} alt=""/><button onClick={()=>setPiece(p.id,'photos',(f.pieces[p.id]?.photos||[]).filter((_,j)=>j!==i))} style={{position:'absolute',top:-4,right:-4,width:16,height:16,borderRadius:'50%',background:'var(--dg)',color:'white',border:'none',fontSize:10,cursor:'pointer'}}>×</button></div>))}
              <button onClick={()=>{
                const inp=document.createElement('input');
                inp.type='file';inp.accept='image/*';inp.capture='environment';
                let done=false,safety;
                const doResume=()=>{if(done)return;done=true;clearTimeout(safety);window.removeEventListener('focus',onFocusBack);setTimeout(()=>resumeRealtime(),1200);};
                const onFocusBack=()=>{setTimeout(doResume,1500);};
                safety=setTimeout(doResume,20000);
                inp.onchange=e=>{
                  if(e.target.files[0]){
                    const file=e.target.files[0];
                    const add=u=>setPiece(p.id,'photos',[...(f.pieces[p.id]?.photos||[]),u]);
                    compressToDataUrl(file,{maxDim:1280,quality:0.7}).then(add).catch(()=>{
                      compressToDataUrl(file,{maxDim:800,quality:0.45}).then(add).catch(()=>window.alert('La photo n\'a pas pu être traitée par le téléphone (format ou taille). Réessaie de la reprendre. Si ça recommence, préviens la direction.'));
                    });
                  }
                  doResume();
                };
                window.addEventListener('focus',onFocusBack);
                suspendRealtime();
                inp.click();
              }} style={{width:54,height:54,borderRadius:8,border:'2px dashed var(--li)',background:'var(--bg)',cursor:'pointer',fontSize:20,color:'var(--si)',display:'flex',alignItems:'center',justifyContent:'center'}}>📷</button>
            </div>
          </div>
          {/* ── v2 : checklist items du rapport de ménage (logique inversée) ── */}
          {isV2&&(p._items||[]).length>0&&<div style={{marginTop:12,borderTop:'1px dashed var(--li)',paddingTop:10}}>
            <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.04em',color:'var(--si)',marginBottom:6}}>Items du rapport de ménage</div>
            {p._items.map(it=>{
              const iid=it.id;
              const flagged=!!f.items?.[iid];
              return(<div key={iid} style={{marginBottom:flagged?8:4}}>
                <div onClick={()=>toggleItem(iid)} style={{display:'flex',gap:8,alignItems:'center',padding:'6px 8px',borderRadius:8,cursor:'pointer',background:flagged?'#fde0e0':'transparent',border:flagged?'1.5px solid var(--dg)':'1.5px solid transparent'}}>
                  <span style={{width:22,height:22,borderRadius:6,border:`2px solid ${flagged?'var(--dg)':'var(--li)'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0,background:flagged?'var(--dg)':'var(--wh)',color:'white',fontWeight:700}}>{flagged?'✗':''}</span>
                  <span style={{fontSize:12.5,color:flagged?'var(--dg)':'var(--ink)',fontWeight:flagged?700:400}}>{it.lb||it.l||iid}</span>
                </div>
                {flagged&&<div style={{marginLeft:30,display:'flex',flexDirection:'column',gap:4,marginTop:4}}>
                  <input type="text" value={f.items[iid]?.obs||''} onChange={e=>setItem(iid,'obs',e.target.value)} placeholder="Commentaire (optionnel)" style={{fontSize:11,padding:'4px 8px',borderRadius:6,border:'1px solid var(--li)'}}/>
                  <div style={{display:'flex',gap:4,alignItems:'center'}}>
                    {(f.items[iid]?.photos||[]).map((ph,j)=>(<div key={j} style={{position:'relative'}}><img src={ph} style={{width:36,height:36,objectFit:'cover',borderRadius:6}} alt=""/><button onClick={()=>setItem(iid,'photos',(f.items[iid]?.photos||[]).filter((_,k)=>k!==j))} style={{position:'absolute',top:-3,right:-3,width:14,height:14,borderRadius:'50%',background:'var(--dg)',color:'white',border:'none',fontSize:8,cursor:'pointer'}}>×</button></div>))}
                    <button onClick={()=>{
                      const inp2=document.createElement('input');
                      inp2.type='file';inp2.accept='image/*';inp2.capture='environment';
                      let done2=false,safety2;
                      const doResume2=()=>{if(done2)return;done2=true;clearTimeout(safety2);window.removeEventListener('focus',onFocus2);setTimeout(()=>resumeRealtime(),1200);};
                      const onFocus2=()=>{setTimeout(doResume2,1500);};
                      safety2=setTimeout(doResume2,20000);
                      inp2.onchange=ev=>{
                        if(ev.target.files[0]){
                          const file2=ev.target.files[0];
                          const add2=u=>setItem(iid,'photos',[...(f.items[iid]?.photos||[]),u]);
                          compressToDataUrl(file2,{maxDim:1024,quality:0.6}).then(add2).catch(()=>{
                            compressToDataUrl(file2,{maxDim:800,quality:0.45}).then(add2).catch(()=>window.alert('Photo impossible.'));
                          });
                        }
                        doResume2();
                      };
                      window.addEventListener('focus',onFocus2);
                      suspendRealtime();
                      inp2.click();
                    }} style={{width:36,height:36,borderRadius:6,border:'1.5px dashed var(--li)',background:'var(--bg)',cursor:'pointer',fontSize:14,color:'var(--si)',display:'flex',alignItems:'center',justifyContent:'center'}}>📷</button>
                  </div>
                </div>}
              </div>);
            })}
            {(()=>{const nc=p._items.filter(it=>!!f.items?.[it.id]).length;return nc>0?<div style={{fontSize:11,fontWeight:700,color:'var(--dg)',background:'#fde0e0',borderRadius:6,padding:'4px 8px',marginTop:4,display:'inline-block'}}>{'⚠\uFE0F '+nc+' non-conformit'+(nc>1?'és':'é')+' sur '+p._items.length+' items'}</div>:null;})()}
          </div>}
        </div>))}
        {scoreGlobal&&<div style={{background:'var(--bg)',borderRadius:10,padding:'10px',textAlign:'center'}}><div style={{fontSize:11,color:'var(--si)',marginBottom:3}}>Score moyen</div><div style={{fontFamily:'Fredoka,cursive',fontSize:26,color:scoreColor,fontWeight:700}}>{scoreGlobal}<span style={{fontSize:13}}>/4</span></div></div>}
        {isV2&&<div style={{background:nonConf>0?'#fde0e0':'#e1f7ee',borderRadius:10,padding:'10px',textAlign:'center'}}><div style={{fontSize:11,color:'var(--si)',marginBottom:3}}>Non-conformités rapport</div><div style={{fontFamily:'Fredoka,cursive',fontSize:22,color:nonConf>0?'var(--dg)':'var(--ok)',fontWeight:700}}>{nonConf}<span style={{fontSize:13,color:'var(--si)'}}> / {totalItems} items</span></div></div>}
      </div>}
      {/* VÉRIFICATIONS */}
      {step===1&&<div style={{display:'flex',flexDirection:'column',gap:8}}>
        <div style={{fontSize:11,color:'var(--si)',fontStyle:'italic',marginBottom:4}}>Points de contrôle finaux avant verdict.</div>
        {CTRL_CHECKS.map(c=>(<div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'13px 14px',background:'var(--wh)',borderRadius:12,border:`1.5px solid ${f.checks[c.id]===true?'var(--mn)':f.checks[c.id]===false?'var(--dg)':'var(--li)'}`}}>
          <span style={{fontSize:13,fontWeight:600,flex:1,paddingRight:12}}>{c.label}</span>
          <div style={{display:'flex',gap:6,flexShrink:0}}>
            <button onClick={()=>setCheck(c.id,true)} style={{padding:'5px 12px',borderRadius:999,border:'2px solid',fontSize:12,fontWeight:700,cursor:'pointer',borderColor:f.checks[c.id]===true?'var(--mn)':'var(--li)',background:f.checks[c.id]===true?'var(--mn)':'var(--wh)',color:f.checks[c.id]===true?'white':'var(--si)'}}>✓ Oui</button>
            <button onClick={()=>setCheck(c.id,false)} style={{padding:'5px 12px',borderRadius:999,border:'2px solid',fontSize:12,fontWeight:700,cursor:'pointer',borderColor:f.checks[c.id]===false?'var(--dg)':'var(--li)',background:f.checks[c.id]===false?'#fde0e0':'var(--wh)',color:f.checks[c.id]===false?'var(--dg)':'var(--si)'}}>✗ Non</button>
          </div>
        </div>))}
        <div style={{marginTop:6}}><label className="fl">Notes globales</label><textarea rows="3" value={f.notes||''} onChange={e=>set('notes',e.target.value)} placeholder="Observations générales, instructions pour l'équipe…"/></div>
      </div>}
      {/* VERDICT */}
      {step===2&&<div style={{display:'flex',flexDirection:'column',gap:12}}>
        {scoreGlobal&&<div style={{background:'var(--bg)',borderRadius:10,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}><div style={{fontSize:12,color:'var(--si)'}}>Score global des pièces</div><div style={{fontFamily:'Fredoka,cursive',fontSize:22,color:scoreColor,fontWeight:700}}>{scoreGlobal}/4</div></div>}
        {isV2&&<div style={{background:nonConf>0?'#fde0e0':'#e1f7ee',borderRadius:10,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}><div style={{fontSize:12,color:'var(--si)'}}>Non-conformités rapport</div><div style={{fontFamily:'Fredoka,cursive',fontSize:22,color:nonConf>0?'var(--dg)':'var(--ok)',fontWeight:700}}>{nonConf} <span style={{fontSize:13,color:'var(--si)'}}>/ {totalItems}</span></div></div>}
        <div style={{fontSize:11,color:'var(--si)',fontStyle:'italic',padding:'6px 10px',background:'#e8f4ff',borderRadius:8}}>📝 Cette décision est enregistrée avec votre nom et l'heure exacte.</div>
        {saving&&<div style={{fontSize:12.5,fontWeight:700,color:'#7a5a00',background:'#fff9e0',border:'1.5px solid var(--ye)',borderRadius:10,padding:'11px 13px'}}>
          ⏳ Enregistrement sur cet appareil — ne fermez pas l'application.
        </div>}
        {[
          {v:'pret',    icon:'✅',label:'Prêt pour le check-in',     desc:"L'appartement est validé.",                         border:'var(--mn)',bg:'#e1f7ee',c:'#1e6e3d'},
          {v:'nonpret', icon:'⚠️',label:'Pas prêt — problème non résolu',desc:'Une action est requise avant le check-in.',       border:'var(--dg)',bg:'#fde0e0',c:'var(--dg)'},
          {v:'reporter',icon:'📅',label:'Reporter le contrôle au lendemain',desc:"Le contrôle lui-même est renvoyé à J+1.", border:'var(--ye)',bg:'#fff9e0',c:'#7a5a00'},
        ].map(({v,icon,label,desc,border,bg,c})=>(
          <button key={v} onClick={()=>doVerdict(v)} disabled={saving} style={{padding:'16px',borderRadius:14,border:`2.5px solid ${border}`,background:bg,cursor:saving?'not-allowed':'pointer',opacity:saving&&savingWhat!==v?.45:1,textAlign:'left',display:'flex',gap:12,alignItems:'center',width:'100%'}}>
            <span style={{fontSize:28,flexShrink:0}}>{savingWhat===v?'⏳':icon}</span>
            <div><div style={{fontFamily:'Fredoka,cursive',fontSize:16,color:c,marginBottom:3}}>{savingWhat===v?'Enregistrement…':label}</div><div style={{fontSize:11,color:'var(--si)'}}>{savingWhat===v?"Ne fermez pas l'application.":desc}</div></div>
          </button>
        ))}
        {(f.history||[]).length>0&&<div>
          <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--si)',marginBottom:7}}>Historique</div>
          {f.history.map((h,i)=>(<div key={i} style={{fontSize:11,padding:'6px 10px',background:'var(--bg)',borderRadius:7,marginBottom:4,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontWeight:700}}>{h.action==='pret'?'✅ Validé':h.action==='nonpret'?'⚠️ Non prêt':'📅 Reporté'} — {h.by}</span>
            <span style={{color:'var(--si)'}}>{ft(h.at)}</span>
          </div>))}
        </div>}
      </div>}
    </div>
    {/* Navigation */}
    <div style={{display:'flex',justifyContent:'space-between',paddingTop:12,borderTop:'1px solid var(--li)',marginTop:10}}>
      <button onClick={()=>step>0?setStep(s=>s-1):onClose()} disabled={saving} className="btn bgh">{step===0?'Annuler':'← Retour'}</button>
      {step<2&&<button onClick={()=>setStep(s=>s+1)} className="btn bor">Suivant →</button>}
      {step===2&&<button onClick={()=>commitSave(enrichV2(f),false,'draft')} disabled={saving} className="btn bgh">{savingWhat==='draft'?'⏳ Enregistrement…':'Sauvegarder sans verdict'}</button>}
    </div>
  </div>);
}

// ══════════════ SECTION ADMIN : Rapports de ménage ══════════════
// Vue centralisée de TOUS les rapports (brouillons + soumis + contrôlés), avec filtres et export PDF.
function SecRapports({rep,setRep,biens,staff,gerants,controles,als,setControles,sess,dureeAnom}){
  const[filterBien,setFilterBien]=useState('');
  const[filterEtat,setFilterEtat]=useState('all'); // all|draft|submitted|controlled
  const[search,setSearch]=useState('');
  const[showHidden,setShowHidden]=useState(false);
  const[selR,setSelR]=useState(null);
  const[editR,setEditR]=useState(null); // rapport en cours de modification admin (réutilise RapportModal)
  // ── Modification du contrôle associé, directement depuis le détail du rapport (v151) ──
  // Avant, il fallait sortir vers Contrôles → Archives → « Voir » pour éditer un contrôle déjà
  // clôturé. Même formulaire (CtrlModal) et même logique de sauvegarde que SecControles.saveCtrl.
  const[editCtrl,setEditCtrl]=useState(null);
  const[ctrlDirty,setCtrlDirty]=useState(false);
  const closeCtrl=()=>{
    if(ctrlDirty&&!window.confirm('Ce contrôle a des modifications non enregistrées (photos, notes…). Fermer sans sauvegarder ?')) return;
    setCtrlDirty(false);
    setEditCtrl(null);
  };
  const saveCtrl=(ctrl,close)=>{
    setControles(prev=>{const i=prev.findIndex(x=>x.id===ctrl.id);return i>=0?prev.map(x=>x.id===ctrl.id?ctrl:x):[...prev,ctrl];});
    setRep(prev=>appliquerMarquageControle(prev,ctrl,{ecraser:true}));
    if(close) setEditCtrl(null);
  };
  // Sauvegarde d'une modification admin. On réutilise le formulaire RapportModal (le même que le salarié),
  // mais on PRÉSERVE les métadonnées d'origine du rapport édité : auteur (sid), date, état de soumission (sub),
  // identifiant. RapportModal.save() écrase ces champs (date:today(), sub:now()/null) → on les réinjecte ici.
  // Modif silencieuse : pas d'historique, pas de notif rapport_soumis (le rapport était déjà soumis/contrôlé).
  // v240 : « Soumettre » sur un BROUILLON le passe en soumis (avant, sub était écrasé par l'ancienne valeur vide) ;
  // la certification du salarié (engage) n'est jamais enregistrée par l'admin. Logique pure : rapportAdminEdit.js.
  const saveEditR=(r,fin)=>{
    const merged=fusionnerRapportAdmin(editR,r,fin,now());
    setRep(prev=>prev.map(x=>x.id===merged.id?merged:x));
    setEditR(null);
  };
  // ─── MIGRATION PHOTOS VERS STORAGE (panneau ponctuel, disparaît quand tout est migré) ───
  const[migRunning,setMigRunning]=useState(false);
  const[migMsg,setMigMsg]=useState('');
  const photosToMigrate=useMemo(()=>countBase64Photos(rep),[rep]);
  const runMigration=async()=>{
    if(!window.confirm(`Migrer ${photosToMigrate} photo(s) base64 vers Supabase Storage ?\n\nLa page va se recharger automatiquement à la fin.\nPendant l'opération, demande aux salariés de ne pas soumettre de rapport.\n\nL'opération est sûre : tu peux la relancer si elle plante en cours.`))return;
    setMigRunning(true);
    setMigMsg('Démarrage…');
    try{
      const result=await migrateAllPhotosToStorage(({processed,total,currentRep})=>{
        setMigMsg(`Photo ${processed} / ${total} — Rapport …${(currentRep||'').slice(-6)}`);
      });
      const okMsg=`✅ Migration terminée : ${result.migrated} / ${result.total} photos migrées.`+(result.errors>0?` ${result.errors} échec(s) — relance pour réessayer.`:'');
      setMigMsg(okMsg);
      alert(okMsg+'\n\nLa page va se recharger pour rafraîchir les rapports.');
      window.location.reload();
    }catch(e){
      setMigMsg(`❌ Erreur : ${e.message}`);
      setMigRunning(false);
    }
  };

  // ── Même mécanisme pour les contrôles qualité (controles7, session 11/08/2026) ──
  const[migCtrlRunning,setMigCtrlRunning]=useState(false);
  const[migCtrlMsg,setMigCtrlMsg]=useState('');
  const ctrlPhotosToMigrate=useMemo(()=>countBase64PhotosCtrl(controles),[controles]);
  const runMigrationCtrl=async()=>{
    if(!window.confirm(`Migrer ${ctrlPhotosToMigrate} photo(s) de contrôle base64 vers Supabase Storage ?\n\nLa page va se recharger automatiquement à la fin.\nPendant l'opération, demande de ne pas soumettre de contrôle.\n\nL'opération est sûre : tu peux la relancer si elle plante en cours.`))return;
    setMigCtrlRunning(true);
    setMigCtrlMsg('Démarrage…');
    try{
      const result=await migrateAllPhotosToStorageCtrl(({processed,total,currentCtrl})=>{
        setMigCtrlMsg(`Photo ${processed} / ${total} — Contrôle …${(currentCtrl||'').slice(-6)}`);
      });
      const okMsg=`✅ Migration terminée : ${result.migrated} / ${result.total} photos migrées.`+(result.errors>0?` ${result.errors} échec(s) — relance pour réessayer.`:'');
      setMigCtrlMsg(okMsg);
      alert(okMsg+'\n\nLa page va se recharger pour rafraîchir les contrôles.');
      window.location.reload();
    }catch(e){
      setMigCtrlMsg(`❌ Erreur : ${e.message}`);
      setMigCtrlRunning(false);
    }
  };

  // Liste combinée staff + gérants pour retrouver le "salarié" qui a fait le rapport
  const allWorkers=[...(staff||[]),...((gerants||[]).map(g=>({...g,_gerant:true})))];

  // Actions de suppression
  const hideForAdmin=r=>{
    if(!window.confirm(`Masquer ce rapport sur l'interface admin ?\n\nIl reste visible pour le propriétaire. Tu pourras le retrouver en cochant "Voir aussi les masqués".`))return;
    setRep(prev=>prev.map(x=>x.id===r.id?{...x,hidAdmin:true}:x));
    setSelR(null);
  };
  const unhideForAdmin=r=>{
    setRep(prev=>prev.map(x=>x.id===r.id?{...x,hidAdmin:false}:x));
  };
  const deleteForEveryone=r=>{
    if(!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ce rapport ?\n\nIl disparaîtra :\n• De l'interface admin\n• De l'interface propriétaire\n• Du contrôle associé (si présent)\n\nCette action est irréversible.`))return;
    setRep(prev=>prev.filter(x=>x.id!==r.id));
    setSelR(null);
  };

  const rapports=(rep||[]).map(r=>{
    const bien=biens.find(b=>b.id===r.bid);
    const worker=allWorkers.find(s=>s.id===r.sid);
    const ctrl=(controles||[]).find(c=>c.repId===r.id);
    const etat=!r.sub?'draft':(ctrl?.verdict==='pret'||ctrl?.verdict==='nonpret')?'controlled':'submitted';
    // Lien vers un signalement de seuil qualité (dureeAnom7), via tid — v158-2, 14/08/2026
    const duree=(dureeAnom||[]).find(a=>a.tid===r.tid);
    return{...r,_bien:bien,_worker:worker,_ctrl:ctrl,_etat:etat,_duree:duree};
  }).filter(r=>{
    if(!showHidden&&r.hidAdmin)return false;
    if(filterBien&&r.bid!==filterBien)return false;
    if(filterEtat!=='all'&&r._etat!==filterEtat)return false;
    if(search){
      const s=search.toLowerCase();
      if(!(r._bien?.name||'').toLowerCase().includes(s)&&!(fname(r._worker)||'').toLowerCase().includes(s))return false;
    }
    return true;
  }).sort((a,b)=>(b.sub||b.date||'').localeCompare(a.sub||a.date||''));

  const stats={
    total:(rep||[]).filter(r=>!r.hidAdmin).length,
    draft:(rep||[]).filter(r=>!r.hidAdmin&&!r.sub).length,
    submitted:(rep||[]).filter(r=>!r.hidAdmin&&r.sub).length,
    controlled:(rep||[]).filter(r=>{if(r.hidAdmin)return false;const c=(controles||[]).find(x=>x.repId===r.id);return c?.verdict==='pret'||c?.verdict==='nonpret';}).length,
    hidden:(rep||[]).filter(r=>r.hidAdmin).length,
  };

  const ET={draft:{l:'📝 Brouillon',bg:'#fff9e0',c:'#7a5a00'},submitted:{l:'✅ Soumis',bg:'#e1f7ee',c:'#1e6e3d'},controlled:{l:'🔍 Contrôlé',bg:'#dbeeff',c:'#1e5fa8'}};

  return(<div>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:8}}>
      <div className="D" style={{fontSize:18}}>Rapports de ménage</div>
      <div style={{fontSize:11,color:'var(--si)'}}>{rapports.length} rapport(s) affiché(s){stats.hidden>0?` · ${stats.hidden} masqué(s)`:''}</div>
    </div>

    {/* Compteurs */}
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8,marginBottom:14}}>
      {[['Total',stats.total,'var(--or)','📋'],['Brouillons',stats.draft,'#7a5a00','📝'],['Soumis',stats.submitted,'var(--ok)','✅'],['Contrôlés',stats.controlled,'var(--bl)','🔍']].map(([l,v,c,e])=>(
        <div key={l} className="card" style={{padding:'8px 12px',borderLeft:`3px solid ${c}`}}>
          <div style={{fontSize:10,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.06em'}}>{e} {l}</div>
          <div style={{fontSize:20,fontWeight:700,color:c}}>{v}</div>
        </div>
      ))}
    </div>

    {/* PHOTOS NON SYNCHRONISÉES — alerte rouge tant qu'il reste des photos base64 dans rep7.
        Couvre 2 cas : (a) échec d'upload terrain (connexion salarié instable), (b) anciennes photos non migrées.
        Dans les deux cas, l'action est la même : relancer l'envoi vers Storage via le bouton ci-dessous. */}
    {(photosToMigrate>0||migRunning||migMsg)&&(()=>{
      // Détail : quels rapports ont encore des photos en base64 (pour savoir lesquels reprendre)
      const repsConcernes=(rep||[]).map(r=>{
        const n=Object.values(r.pk||{}).filter(v=>typeof v==='string'&&v.startsWith('data:image')).length
               +Object.values(r.gp||{}).filter(v=>typeof v==='string'&&v.startsWith('data:image')).length;
        if(n===0)return null;
        const b=biens.find(x=>x.id===r.bid);
        const w=allWorkers.find(s=>s.id===r.sid);
        return{id:r.id,n,bienName:b?.name||'(bien supprimé)',worker:fname(w)||'?',date:r.date};
      }).filter(Boolean).sort((a,b)=>(b.date||'').localeCompare(a.date||''));
      const alerte=photosToMigrate>0;
      return(
      <div className="card" style={{padding:'12px 14px',marginBottom:14,borderLeft:`3px solid ${alerte?'var(--dg)':'var(--or)'}`,background:alerte?'#fde0e0':'#fff9e0'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <div style={{flex:'1 1 220px'}}>
            <div style={{fontWeight:700,fontSize:13,color:alerte?'var(--dg)':'#7a5a00'}}>
              {alerte?`🚨 ${photosToMigrate} photo(s) non synchronisée(s)`:'🔄 Synchronisation photos'}
            </div>
            <div style={{fontSize:11,color:'var(--si)',marginTop:3}}>
              {photosToMigrate>0
                ? `Ces photos sont encore stockées localement (base64) et n'ont pas été envoyées au serveur — souvent à cause d'une connexion instable côté salarié. Tant qu'elles ne sont pas envoyées, elles alourdissent la base et peuvent bloquer les sauvegardes.`
                : 'Toutes les photos sont sur le serveur. ✅'}
            </div>
            {migMsg&&<div style={{fontSize:11,marginTop:4,fontFamily:'monospace',color:alerte?'var(--dg)':'#7a5a00'}}>{migMsg}</div>}
          </div>
          <button onClick={runMigration} disabled={migRunning||photosToMigrate===0} className="btn bor sm" style={alerte?{borderColor:'var(--dg)',color:'var(--dg)'}:{}}>
            {migRunning?'⏳ Envoi en cours…':'🚀 Envoyer les photos'}
          </button>
        </div>
        {repsConcernes.length>0&&(
          <div style={{marginTop:10,paddingTop:8,borderTop:'1px dashed #f5b5b5',fontSize:11,color:'#7f1d1d',lineHeight:1.6}}>
            <div style={{fontWeight:700,marginBottom:3}}>Rapports concernés :</div>
            {repsConcernes.map(rc=>(
              <div key={rc.id}>• <strong>{rc.bienName}</strong> · {rc.worker} · {rc.date?fd(rc.date):'?'} — {rc.n} photo(s)</div>
            ))}
            <div style={{fontStyle:'italic',marginTop:5,opacity:.85}}>Astuce : le salarié peut aussi rouvrir son rapport depuis une bonne connexion et le ré-enregistrer — les photos partiront seules.</div>
          </div>
        )}
      </div>);
    })()}

    {/* PHOTOS DE CONTRÔLE NON SYNCHRONISÉES — même mécanisme que ci-dessus, pour controles7
        (session 11/08/2026, cause racine des contrôles perdus signalés par Claire). */}
    {(ctrlPhotosToMigrate>0||migCtrlRunning||migCtrlMsg)&&(()=>{
      const ctrlsConcernes=(controles||[]).map(c=>{
        let n=0;
        for(const p of Object.values(c.pieces||{}))for(const v of((p&&p.photos)||[]))if(typeof v==='string'&&v.startsWith('data:image'))n++;
        if(n===0)return null;
        const b=biens.find(x=>x.id===c.bid);
        return{id:c.id,n,bienName:b?.name||'(bien supprimé)',date:c.closedAt||''};
      }).filter(Boolean).sort((a,b)=>(b.date||'').localeCompare(a.date||''));
      const alerte=ctrlPhotosToMigrate>0;
      return(
      <div className="card" style={{padding:'12px 14px',marginBottom:14,borderLeft:`3px solid ${alerte?'var(--dg)':'var(--or)'}`,background:alerte?'#fde0e0':'#fff9e0'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <div style={{flex:'1 1 220px'}}>
            <div style={{fontWeight:700,fontSize:13,color:alerte?'var(--dg)':'#7a5a00'}}>
              {alerte?`🚨 ${ctrlPhotosToMigrate} photo(s) de contrôle non synchronisée(s)`:'🔄 Synchronisation photos de contrôle'}
            </div>
            <div style={{fontSize:11,color:'var(--si)',marginTop:3}}>
              {ctrlPhotosToMigrate>0
                ? `Ces photos de contrôle sont encore stockées localement (base64) et alourdissent le document controles7 dans son ensemble — cause probable des sauvegardes de contrôle qui échouent ou traînent.`
                : 'Toutes les photos de contrôle sont sur le serveur. ✅'}
            </div>
            {migCtrlMsg&&<div style={{fontSize:11,marginTop:4,fontFamily:'monospace',color:alerte?'var(--dg)':'#7a5a00'}}>{migCtrlMsg}</div>}
          </div>
          <button onClick={runMigrationCtrl} disabled={migCtrlRunning||ctrlPhotosToMigrate===0} className="btn bor sm" style={alerte?{borderColor:'var(--dg)',color:'var(--dg)'}:{}}>
            {migCtrlRunning?'⏳ Envoi en cours…':'🚀 Envoyer les photos'}
          </button>
        </div>
        {ctrlsConcernes.length>0&&(
          <div style={{marginTop:10,paddingTop:8,borderTop:'1px dashed #f5b5b5',fontSize:11,color:'#7f1d1d',lineHeight:1.6}}>
            <div style={{fontWeight:700,marginBottom:3}}>Contrôles concernés :</div>
            {ctrlsConcernes.map(cc=>(
              <div key={cc.id}>• <strong>{cc.bienName}</strong> · {cc.date?fd(cc.date):'?'} — {cc.n} photo(s)</div>
            ))}
          </div>
        )}
      </div>);
    })()}

    {/* Filtres */}
    <div className="card" style={{padding:'10px 12px',marginBottom:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Rechercher (bien, salarié)…" style={{flex:'1 1 180px',fontSize:12,padding:'6px 10px'}}/>
      <select value={filterBien} onChange={e=>setFilterBien(e.target.value)} style={{fontSize:12,padding:'6px 8px'}}>
        <option value="">Tous les biens</option>
        {biens.sort((a,b)=>a.name.localeCompare(b.name)).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <div style={{display:'flex',gap:4}}>
        {[['all','Tous'],['draft','📝 Brouillons'],['submitted','✅ Soumis'],['controlled','🔍 Contrôlés']].map(([v,l])=>(
          <button key={v} onClick={()=>setFilterEtat(v)} className={`btn sm ${filterEtat===v?'bor':'bgh'}`} style={{fontSize:11}}>{l}</button>
        ))}
      </div>
      {stats.hidden>0&&<label style={{display:'flex',alignItems:'center',gap:5,fontSize:11,cursor:'pointer',color:'var(--si)'}} onClick={()=>setShowHidden(!showHidden)}>
        <span>{showHidden?'☑':'☐'}</span> Voir aussi les masqués ({stats.hidden})
      </label>}
    </div>

    {/* Liste */}
    {rapports.length===0?<div className="empty">Aucun rapport ne correspond aux filtres.</div>
    :<div style={{display:'flex',flexDirection:'column',gap:6}}>
      {rapports.map(r=>{
        const et=ET[r._etat]||ET.draft;
        const struct=getRapportStructure(r._bien);
        const valid=Object.entries(r.cks||{}).filter(([_,v])=>v).length;
        const total=struct.items.length;
        return(<div key={r.id} className="card" style={{padding:'10px 12px',borderLeft:`3px solid ${et.c}`,opacity:r.hidAdmin?.6:1}}>
          <div style={{display:'flex',justifyContent:'space-between',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',marginBottom:3}}>
                <span style={{fontWeight:700,fontSize:13}}>{r._bien?.name||'(bien supprimé)'}</span>
                <span style={{background:et.bg,color:et.c,fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>{et.l}</span>
                {r.hidAdmin&&<span style={{background:'#f0f0f0',color:'var(--si)',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>🙈 Masqué</span>}
                {r.mrV==='v2'&&<span style={{background:'#e1f7ee',color:'#1e6e3d',fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:999,border:'1px solid var(--mn)'}}>✨ perso</span>}
                {r.ecarts?.length>0&&<span style={{background:'#fff4e8',color:'var(--or)',fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:999,border:'1px solid var(--or)'}}>⚠️ {r.ecarts.length} écart{r.ecarts.length>1?'s':''}</span>}
                {r._duree&&<span title={r._duree.motif} style={{background:r._duree.traite?'#eef1f3':'#fff4d6',color:r._duree.traite?'var(--si)':'#7a5a00',fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:999,border:`1px solid ${r._duree.traite?'var(--li)':'var(--or)'}`}}>⏱️ Seuil qualité{r._duree.traite?' (traité)':''}</span>}
                {r._ctrl?.verdict==='pret'&&<span style={{fontSize:10,color:'var(--ok)',fontWeight:600}}>Verdict : Prêt</span>}
                {r._ctrl?.verdict==='nonpret'&&<span style={{fontSize:10,color:'var(--dg)',fontWeight:600}}>Verdict : Non prêt</span>}
              </div>
              <div style={{fontSize:11,color:'var(--si)'}}>
                {fname(r._worker)||'(compte supprimé)'}{r._worker?._gerant?' (direction)':''} · {r.date?fd(r.date):'?'}
                {r.sub&&<> · soumis le {new Date(r.sub).toLocaleString('fr-FR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</>}
              </div>
              <div style={{fontSize:11,color:'var(--si)',marginTop:2}}>
                {valid}/{total} points · {Object.keys(r.pk||{}).filter(k=>r.pk[k]).length+Object.keys(r.gp||{}).filter(k=>r.gp[k]).length} photo(s)
                {Number(r.billTotal)>0&&<span style={{marginLeft:6,padding:'1px 7px',background:'#fff4e8',color:'var(--or)',fontWeight:700,borderRadius:999,fontSize:10}}>💶 +{feur(Number(r.billTotal))} extras</span>}
              </div>
            </div>
            <div style={{display:'flex',gap:5,flexShrink:0,flexWrap:'wrap'}}>
              <button onClick={()=>setSelR(r)} className="btn bgh sm" title="Voir le détail"><Ic n="ey" c="w-3.5 h-3.5"/></button>
              <button onClick={()=>setEditR(r)} className="btn bor sm" title="Modifier le rapport (cocher/décocher, photos, tâches…)" style={{borderColor:'var(--or)',color:'var(--or)'}} disabled={!r._bien}>✏️ Modifier</button>
              <button onClick={()=>generateRapportPdf({rapport:r,bien:r._bien,worker:r._worker,struct,controle:r._ctrl,dureeAnom:r._duree})} className="btn bor sm" title="Télécharger en PDF">📄 PDF</button>
              {r.hidAdmin
                ?<button onClick={()=>unhideForAdmin(r)} className="btn bgh sm" title="Réafficher" style={{color:'var(--ok)'}}>👁 Réafficher</button>
                :<button onClick={()=>hideForAdmin(r)} className="btn bgh sm" title="Masquer côté admin (reste visible propriétaire)" style={{color:'var(--si)'}}>🙈</button>
              }
              <button onClick={()=>deleteForEveryone(r)} className="btn bgh sm" title="Supprimer définitivement (admin + propriétaire)" style={{color:'var(--dg)'}}>🗑</button>
            </div>
          </div>
        </div>);
      })}
    </div>}

    {/* Modale MODIFICATION rapport (admin) — réutilise le formulaire salarié RapportModal */}
    {editR&&editR._bien&&<M onC={()=>setEditR(null)} title="Modifier le rapport de ménage" col="var(--or)" wide ch={
      <div>
        <div style={{background:'#fff4e8',border:'1px solid var(--or)',borderRadius:8,padding:'8px 11px',marginBottom:12,fontSize:11.5,color:'#8a4b00',lineHeight:1.5}}>
          ✏️ <strong>Modification administrateur.</strong> Tu peux cocher/décocher, ajouter ou retirer des photos, modifier les tâches et la facturation.<br/>
          👉 <strong>Pour enregistrer tes corrections, clique sur « 💾 Brouillon »</strong> (en bas du formulaire). Tes modifications seront enregistrées et l'état d'origine du rapport (soumis / contrôlé) est conservé — il ne repassera <em>pas</em> en brouillon.<br/>
          Le bouton « ✅ Soumettre » impose que tous les points obligatoires soient remplis ; sur un <strong>brouillon</strong>, il passe le rapport en « soumis » (sans notification). La case « Je certifie… » n'est <em>pas</em> enregistrée au nom du salarié quand c'est la direction qui soumet.<br/>
          <span style={{fontSize:11,opacity:.85}}>Auteur d'origine conservé : {fname(editR._worker)||'?'} · date du ménage : {editR.date?fd(editR.date):'?'}.</span>
        </div>
        <ErrorBoundary onClose={()=>setEditR(null)}>
        <RapportModal
          task={{id:editR.tid,bid:editR.bid,type:editR.type||'full'}}
          bien={editR._bien}
          sid={editR.sid}
          existing={editR}
          onSave={saveEditR}
          onClose={()=>setEditR(null)}
        />
        </ErrorBoundary>
      </div>
    }/>}

    {/* Modale détail rapport */}
    {selR&&(()=>{
      const struct=getRapportStructure(selR._bien);
      return<M onC={()=>setSelR(null)} title="Rapport de ménage" col="var(--pk)" wide ch={<div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{background:'var(--cr)',borderRadius:9,padding:'10px 13px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
          <div>
            <div className="D" style={{fontSize:14}}>{selR._bien?.name}</div>
            <div style={{fontSize:11,color:'var(--si)'}}>{fd(selR.date)} · {fname(selR._worker)||'(compte supprimé)'}{selR._worker?._gerant?' (direction)':''}{selR.sub?` · soumis le ${new Date(selR.sub).toLocaleString('fr-FR')}`:''}</div>
            {selR._duree&&<div style={{marginTop:4}}><span title={selR._duree.motif} style={{background:selR._duree.traite?'#eef1f3':'#fff4d6',color:selR._duree.traite?'var(--si)':'#7a5a00',fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:999,border:`1px solid ${selR._duree.traite?'var(--li)':'var(--or)'}`}}>⏱️ Seuil qualité signalé{selR._duree.traite?' (traité)':''}</span></div>}
          </div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            <button onClick={()=>generateRapportPdf({rapport:selR,bien:selR._bien,worker:selR._worker,struct,controle:selR._ctrl,dureeAnom:selR._duree})} className="btn bor sm">📄 PDF rapport</button>
            <button onClick={()=>{setEditR(selR);setSelR(null);}} className="btn bor sm" title="Modifier ce rapport" style={{borderColor:'var(--or)',color:'var(--or)'}} disabled={!selR._bien}>✏️ Modifier</button>
            {selR._ctrl&&(selR._ctrl.verdict==='pret'||selR._ctrl.verdict==='nonpret')&&<button onClick={()=>printControleEtMenage({controle:selR._ctrl,bien:selR._bien,rapport:selR,worker:selR._worker,als,dureeAnom:selR._duree})} className="btn bor sm" style={{borderColor:'var(--mn)',background:'#e1f7ee',color:'#1e6e3d'}}>🔍 PDF contrôle + ménage</button>}
            {selR._ctrl&&<button onClick={()=>setEditCtrl({repId:selR.id,bid:selR.bid,existing:selR._ctrl})} className="btn bor sm" title="Modifier ce contrôle" style={{borderColor:'var(--mn)',color:'#1e6e3d'}}>✏️ Modifier le contrôle</button>}
            {selR.hidAdmin
              ?<button onClick={()=>unhideForAdmin(selR)} className="btn bgh sm" style={{color:'var(--ok)'}}>👁 Réafficher</button>
              :<button onClick={()=>hideForAdmin(selR)} className="btn bgh sm" style={{color:'var(--si)'}}>🙈 Masquer ici</button>
            }
            <button onClick={()=>deleteForEveryone(selR)} className="btn bgh sm" style={{color:'var(--dg)'}}>🗑 Supprimer partout</button>
          </div>
        </div>
        {selR.ecarts?.length>0&&(<div style={{background:'#fff7ed',border:'1.5px solid var(--or)',borderRadius:9,padding:'10px 13px'}}>
          <div style={{fontSize:12,fontWeight:700,color:'var(--or)',marginBottom:6}}>⚠️ {selR.ecarts.length} écart{selR.ecarts.length>1?'s':''} · mode guidé souple</div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            {selR.ecarts.map((e,i)=>(<div key={i} style={{fontSize:11,color:'var(--ink)',display:'flex',gap:6,alignItems:'flex-start'}}>
              <span>{e.type==='piece_hors_ordre'?'🔓':'⏭️'}</span>
              <span>{e.type==='piece_hors_ordre'?<>Pièce <strong>{e.roomName}</strong> ouverte en avance</>:<>Point passé dans <strong>{e.roomName}</strong> : {e.itemLb}</>}</span>
            </div>))}
          </div>
        </div>)}
        {selR._ctrl?.version===2&&(()=>{
          const nc=selR._ctrl.nonConformites||0;
          const tot=selR._ctrl.totalItemsRapport||0;
          const scoreNs=Object.values(selR._ctrl.pieces||{}).map(p=>p?.note).filter(Boolean);
          const avg=scoreNs.length?(scoreNs.reduce((s,n)=>s+n,0)/scoreNs.length).toFixed(1):null;
          const avgCol=avg>=3.5?'var(--ok)':avg>=2.5?'#7a5a00':avg>=1.5?'var(--or)':'var(--dg)';
          return(<div style={{background:nc>0?'#fde0e0':'#e1f7ee',border:`1.5px solid ${nc>0?'var(--dg)':'var(--mn)'}`,borderRadius:9,padding:'10px 13px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:nc>0?'var(--dg)':'var(--ok)'}}>🔍 Contrôle qualité v2 — {nc>0?`${nc} non-conformité${nc>1?'s':''}`:'aucune non-conformité'}</div>
              <div style={{fontSize:11,color:'var(--si)',marginTop:2}}>Verdict : {selR._ctrl.verdict==='pret'?'✅ Prêt':selR._ctrl.verdict==='nonpret'?'⚠️ Non prêt':'—'} · {nc} / {tot} items</div>
            </div>
            {avg&&<div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--si)'}}>Score</div><div style={{fontFamily:'Fredoka,cursive',fontSize:20,color:avgCol,fontWeight:700}}>{avg}/4</div></div>}
          </div>);
        })()}
        {struct.sections.map(sec=>{
          const roomId=sec.room?.id;
          const roomGenPhotos=roomId?struct.genPhotos.filter(p=>p._rmId===roomId):[];
          return(<div key={sec.key} style={{padding:'8px 10px',background:'var(--wh)',borderRadius:8,border:'1px solid var(--li)'}}>
            <div style={{fontSize:struct.useNew?13:10,textTransform:struct.useNew?'none':'uppercase',letterSpacing:struct.useNew?'0':'.08em',fontWeight:700,color:struct.useNew?'var(--or)':'var(--si)',marginBottom:6}}>{sec.title}</div>
            {roomGenPhotos.length>0&&<div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap'}}>{roomGenPhotos.map(ph=>selR.gp?.[ph.id]?<img key={ph.id} src={selR.gp[ph.id]} alt="" style={{width:60,height:60,objectFit:'cover',borderRadius:6,border:'1.5px solid var(--mn)'}}/>:<div key={ph.id} style={{width:60,height:60,borderRadius:6,background:'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'var(--si)'}}>—</div>)}</div>}
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              {sec.items.map(item=>{
                const checked=!!selR.cks?.[item.id];
                const ctrlAnomalie=selR._ctrl?.version===2&&checked&&selR._ctrl.items?.[item.id]?.ok===false;
                const ctrlObs=ctrlAnomalie?selR._ctrl.items[item.id].obs:'';
                return(<div key={item.id} style={{display:'flex',gap:6,alignItems:'flex-start',padding:'3px 0',background:ctrlAnomalie?'#fde0e0':'transparent',borderRadius:ctrlAnomalie?6:0,paddingLeft:ctrlAnomalie?6:0,marginLeft:ctrlAnomalie?-6:0}}>
                <span style={{fontSize:13,minWidth:18}}>{ctrlAnomalie?'⚠️':checked?'✅':'❌'}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:ctrlAnomalie?'var(--dg)':checked?'var(--ink)':'var(--dg)',fontWeight:ctrlAnomalie?700:400}}>{item.lb}{ctrlAnomalie?' — non-conformité au contrôle':''}</div>
                  {ctrlAnomalie&&ctrlObs&&<div style={{fontSize:10.5,color:'var(--dg)',fontStyle:'italic',marginTop:1}}>🔍 {ctrlObs}</div>}
                  {(item.photo||item.pk)&&selR.pk?.[item.id]&&<img src={selR.pk[item.id]} alt="" style={{marginTop:3,width:80,height:80,objectFit:'cover',borderRadius:6,border:'1.5px solid var(--mn)'}}/>}
                  {(item.photo||item.pk)&&!selR.pk?.[item.id]&&<div style={{fontSize:10,color:'var(--ye)',fontStyle:'italic',marginTop:2}}>📷 Photo manquante</div>}
                </div>
              </div>);})}
            </div>
          </div>);
        })}
        {selR.notes&&<div style={{padding:'8px 10px',background:'#fff9e0',borderRadius:8,fontSize:12}}><strong>Notes :</strong> {selR.notes}</div>}
        {/* Détail facturation paramétrable (extras du ménage) — visible admin uniquement */}
        {(()=>{
          const bien=selR._bien;
          const bf=Array.isArray(bien?.bf)?bien.bf:[];
          if(!bien?.bfOn||bf.length===0) return null;
          const lignes=bf.map(field=>{
            const v=(selR.bill||{})[field.id];
            const tu=Number(field.tu)||0;
            if(field.t==='n'){
              const qty=Number(v)||0;
              return{...field,qty,montant:qty*tu,actif:qty>0};
            }
            const checked=v===true;
            return{...field,qty:checked?1:0,montant:checked?tu:0,actif:checked};
          });
          const total=lignes.reduce((s,l)=>s+l.montant,0);
          const actifs=lignes.filter(l=>l.actif);
          return(<div style={{padding:'10px 12px',background:'#fff4e8',borderRadius:8,border:'1.5px solid var(--or)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,flexWrap:'wrap',gap:6}}>
              <strong style={{fontSize:13,color:'var(--or)'}}>💶 Facturation extras</strong>
              <span style={{fontSize:14,fontWeight:700,color:'var(--ok)',fontFamily:'Fredoka,cursive'}}>{feur(total)}</span>
            </div>
            {actifs.length===0?
              <div style={{fontSize:11,color:'var(--si)',fontStyle:'italic'}}>Aucun extra facturé pour ce ménage.</div>
              :<div style={{display:'flex',flexDirection:'column',gap:4}}>
                {actifs.map(l=>(<div key={l.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12,padding:'3px 0',borderBottom:'1px dashed var(--li)'}}>
                  <span><span style={{marginRight:6}}>{l.em||'💰'}</span>{l.lb}{l.t==='n'?` × ${l.qty}`:''}</span>
                  <span style={{color:'var(--si)',fontWeight:600}}>{l.t==='n'?`${l.qty} × ${feur(Number(l.tu)||0)} = `:''}<strong style={{color:'var(--ink)'}}>{feur(l.montant)}</strong></span>
                </div>))}
              </div>}
          </div>);
        })()}
      </div>}/>;
    })()}

    {/* Modale édition contrôle — accessible directement depuis le détail du rapport (v151) */}
    {editCtrl!==null&&<M onC={closeCtrl} title="🔍 Controle appartement" col="var(--mn)" wide ch={<CtrlModal key={editCtrl.existing?.id||editCtrl.repId||editCtrl.bid} repId={editCtrl.repId} bien={biens.find(b=>b.id===editCtrl.bid)} controleurs={[{id:'admin',name:'Admin (direction)'},...staff.filter(s=>s.gouvernante)]} existing={editCtrl.existing} onSave={saveCtrl} onClose={closeCtrl} onDirtyChange={setCtrlDirty} sess={sess}/>}/>}
  </div>);
}


function SecControles({controles,setControles,rep,setRep,biens,staff,gerants,sess,logAction,als,dureeAnom}){
  const[editCtrl,setEditCtrl]=useState(null);
  const[ctrlDirty,setCtrlDirty]=useState(false);
  // Fermeture protégée : confirmation si des modifications non sauvegardées existent
  // (backdrop, croix M, bouton "Annuler" de CtrlModal passent tous par ici).
  const closeCtrl=()=>{
    if(ctrlDirty&&!window.confirm('Ce contrôle a des modifications non enregistrées (photos, notes…). Fermer sans sauvegarder ?')) return;
    setCtrlDirty(false);
    setEditCtrl(null);
  };
  const[tabCtrl,setTabCtrl]=useState('today');
  // Archives filters
  const[archBien,setArchBien]=useState('');
  const[archVerdict,setArchVerdict]=useState('all');
  const[archDateDeb,setArchDateDeb]=useState('');
  const[archDateFin,setArchDateFin]=useState('');
  // Contrôle direct
  const[directBid,setDirectBid]=useState('');

  const gouvernantes=[{id:'admin',name:'Admin (direction)'},...staff.filter(s=>s.gouvernante)];
  // Résolution du salarié auteur du ménage (PDF contrôle, v150) — même construction que SecRapports.
  const allWorkers=[...(staff||[]),...((gerants||[]).map(g=>({...g,_gerant:true})))];
  const todayS=today();
  const heureActuelle=new Date().getHours();
  const rappelSoir=heureActuelle>=19;

  const enAttente=rep.filter(r=>{
    if(!r.sub) return false;
    if(r.hidAdmin) return false;
    const ctrl=controles.find(c=>c.repId===r.id);
    if(ctrl?.verdict==='pret'||ctrl?.verdict==='nonpret'||ctrl?.verdict==='abandonne') return false;
    const soumisAujourdhui=r.sub?.startsWith(todayS)||r.date===todayS;
    const estReporte=ctrl?.verdict==='reporter';
    return soumisAujourdhui||estReporte;
  });

  const cutoff7j=new Date(Date.now()-7*24*3600*1000).toISOString().slice(0,10);
  const faits=controles
    .filter(c=>c.verdict==='pret'||c.verdict==='nonpret')
    .filter(c=>c.closedAt && c.closedAt.slice(0,10)>=cutoff7j)
    .sort((a,b)=>(b.closedAt||'').localeCompare(a.closedAt||''));

  // Archives = TOUS les controles clotures, sans limite de date, avec filtres
  const archives=controles
    .filter(c=>c.verdict==='pret'||c.verdict==='nonpret')
    .filter(c=>{
      if(archBien&&c.bid!==archBien) return false;
      if(archVerdict!=='all'&&c.verdict!==archVerdict) return false;
      if(archDateDeb&&(c.closedAt||'').slice(0,10)<archDateDeb) return false;
      if(archDateFin&&(c.closedAt||'').slice(0,10)>archDateFin) return false;
      return true;
    })
    .sort((a,b)=>(b.closedAt||'').localeCompare(a.closedAt||''));

  // v133 (Lot B) : ces deux boutons (« Reporter », « Abandonner ») n'avaient AUCUNE
  // protection de file locale — dette signalée en v130 et restée ouverte. Un clic
  // pendant une coupure réseau (ou avec un JWT expiré, cas du 04/08/2026) perdait la
  // décision sans aucun moyen de la rejouer, alors que le même geste fait DEPUIS la
  // modale était protégé depuis le Chantier B. Ils passent désormais par
  // enqueueControle, écriture ATTENDUE (bornée) avant de toucher au state.
  //
  // appliquerMarquageControle n'est volontairement PAS appelée ici : la fonction
  // ignore par construction les verdicts 'reporter'/'abandonne' (utils.js L143), les
  // seuls que ce chemin produit. L'appeler serait du code mort trompeur.
  const[rapideBusy,setRapideBusy]=useState(null); // `${repId}|${verdict}` en cours
  const actionRapide=async(repId,bid,verdict)=>{
    if(rapideBusy)return;
    const existing=controles.find(c=>c.repId===repId);
    const entry={action:verdict,by:sess?.name||'Admin',at:now()};
    const ctrl={
      id:existing?.id||'ctrl'+Date.now(),
      repId,bid,verdict,
      pieces:existing?.pieces||{},
      checks:existing?.checks||{},
      notes:existing?.notes||'',
      history:[...(existing?.history||[]),entry],
      closedAt:verdict==='reporter'?null:now(),
      controlleurId:sess?.id||'admin',
    };
    setRapideBusy(`${repId}|${verdict}`);
    await attendreFileLocale(enqueueControle({recordId:ctrl.id,payload:ctrl,sid:sess?.id,authorLabel:sess?.name||'',fin:true}));
    setControles(prev=>{const i=prev.findIndex(x=>x.id===ctrl.id);return i>=0?prev.map(x=>x.id===ctrl.id?ctrl:x):[...prev,ctrl];});
    setRapideBusy(null);
  };

  const saveCtrl=(ctrl,close)=>{
    setControles(prev=>{const i=prev.findIndex(x=>x.id===ctrl.id);return i>=0?prev.map(x=>x.id===ctrl.id?ctrl:x):[...prev,ctrl];});
    // ecraser:true — validation à l'instant : un recontrôle doit remplacer le verdict
    // précédent. Le filtre sur les verdicts définitifs est dans la fonction pure.
    setRep(prev=>appliquerMarquageControle(prev,ctrl,{ecraser:true}));
    if(close) setEditCtrl(null);
  };

  const VColors={pret:{bg:'#e1f7ee',c:'var(--ok)',l:'✅ Prêt'},nonpret:{bg:'#fde0e0',c:'var(--dg)',l:'⚠️ Non prêt'},reporter:{bg:'#fff9e0',c:'#7a5a00',l:'📅 Reporté'},abandonne:{bg:'#f0f0f0',c:'var(--si)',l:'🗑 Abandonné'}};

  // Carte controle reutilisable (aujourd'hui + archives)
  const CarteCtrl=({c})=>{
    const bien=biens.find(b=>b.id===c.bid);
    const r=rep.find(x=>x.id===c.repId);
    const v=VColors[c.verdict]||{};
    const cIsV2=c.version===2;
    const ns=cIsV2
      ?Object.values(c.pieces||{}).map(p=>p?.note).filter(Boolean)
      :CTRL_PIECES.map(p=>c.pieces?.[p.id]?.note).filter(Boolean);
    const avg=ns.length?(ns.reduce((s,n)=>s+n,0)/ns.length).toFixed(1):null;
    const cNonConf=cIsV2?(c.nonConformites||0):null;
    const cTotal=cIsV2?(c.totalItemsRapport||0):null;
    const lastH=c.history?.slice(-1)[0];
    const dateLabel=c.closedAt ? new Date(c.closedAt).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'numeric'})+' '+ft(c.closedAt) : '—';
    // Lien vers un signalement de seuil qualité (dureeAnom7), via le tid du rapport associé — v158-2, 14/08/2026
    const duree=r?(dureeAnom||[]).find(a=>a.tid===r.tid):null;
    // v190 : couleur basée sur le score (pas le verdict) — orange si passable, rouge si mauvais
    const avgF=avg?parseFloat(avg):null;
    const scoreColor=avgF!==null?(avgF>=3?'var(--mn)':avgF>=2?'var(--or)':'var(--dg)'):(c.verdict==='pret'?'var(--mn)':'var(--dg)');
    const scoreBg=avgF!==null?(avgF>=3?'#e1f7ee':avgF>=2?'#fff9e0':'#fde0e0'):(v.bg||'#f0f0f0');
    return(<div className="card" style={{padding:'13px 16px',borderLeft:`3px solid ${scoreColor}`}}>
      <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:10,alignItems:'center'}}>
        <div>
          <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4,flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:14}}>{bien?.name||'Bien supprime'}</span>
            <span style={{background:scoreBg,color:scoreColor,fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:999}}>{v.l}</span>
            {avg&&(()=>{const sc=parseFloat(avg);const sCol=sc>=3.5?'var(--ok)':sc>=2.5?'#7a5a00':sc>=1.5?'var(--or)':'var(--dg)';const sBg=sc>=3.5?'#e1f7ee':sc>=2.5?'#fff9e0':sc>=1.5?'#ffe9d6':'#fde0e0';return<span style={{fontSize:11,color:sCol,background:sBg,padding:'2px 7px',borderRadius:999,fontWeight:700}}>Score {avg}/4</span>;})()}
            {cIsV2&&cNonConf!==null&&<span style={{fontSize:11,color:cNonConf>0?'var(--dg)':'var(--ok)',background:cNonConf>0?'#fde0e0':'#e1f7ee',padding:'2px 7px',borderRadius:999,fontWeight:700}}>{cNonConf>0?('\u26A0\uFE0F '+cNonConf+' non-conf. / '+cTotal):('\u2705 0 non-conf.')}</span>}
            {!c.repId&&<span style={{fontSize:10,color:'#92400e',background:'#fffbeb',padding:'2px 7px',borderRadius:999,border:'1px solid #fcd34d'}}>Sans rapport</span>}
            {duree&&<span title={duree.motif} style={{background:duree.traite?'#eef1f3':'#fff4d6',color:duree.traite?'var(--si)':'#7a5a00',fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:999,border:`1px solid ${duree.traite?'var(--li)':'var(--or)'}`}}>⏱️ Seuil qualité{duree.traite?' (traité)':''}</span>}
          </div>
          <div style={{fontSize:11,color:'var(--si)'}}>Par {lastH?.by||'—'} · {dateLabel}</div>
          {r&&<div style={{fontSize:11,color:'var(--si)',marginTop:1}}>Rapport du {r.date?fd(r.date):'—'}</div>}
          {c.notes&&<div style={{fontSize:11,color:'var(--si)',fontStyle:'italic',marginTop:3}}>{c.notes.slice(0,120)}{c.notes.length>120?'…':''}</div>}
        </div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          <button onClick={()=>setEditCtrl({repId:c.repId,bid:c.bid,existing:c})} className="btn bgh sm">Voir</button>
          <button onClick={()=>printControleEtMenage({controle:c,bien,rapport:r,worker:allWorkers.find(s=>s.id===r?.sid),als,dureeAnom:duree})} className="btn bor sm" title="Telecharger le controle + le menage associe en PDF">📄 PDF + ménage</button>
        </div>
      </div>
    </div>);
  };

  const nbAttente=enAttente.length;
  const nbArchives=controles.filter(c=>c.verdict==='pret'||c.verdict==='nonpret').length;

  return(<div>
    {/* Onglets */}
    <div style={{display:'flex',gap:6,marginBottom:18,flexWrap:'wrap'}}>
      <button onClick={()=>setTabCtrl('today')} className={`btn ${tabCtrl==='today'?'bmn':'bgh'} sm`} style={{position:'relative'}}>
        🔍 Controles du jour
        {nbAttente>0&&<span style={{position:'absolute',top:-7,right:-7,background:'var(--dg)',color:'white',borderRadius:'50%',width:18,height:18,fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>{nbAttente}</span>}
      </button>
      <button onClick={()=>setTabCtrl('direct')} className={`btn ${tabCtrl==='direct'?'bmn':'bgh'} sm`}>
        ➕ Controle direct
      </button>
      <button onClick={()=>setTabCtrl('archives')} className={`btn ${tabCtrl==='archives'?'bmn':'bgh'} sm`}>
        🗂 Archives ({nbArchives})
      </button>
    </div>

    {/* ONG. AUJOURD'HUI */}
    {tabCtrl==='today'&&<div>
      {rappelSoir&&nbAttente>0&&<div style={{background:'#fde0e0',border:'1.5px solid var(--dg)',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',gap:12,alignItems:'center'}}>
        <span style={{fontSize:24}}>🔔</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:13,color:'var(--dg)'}}>Rappel de fin de journée</div>
          <div style={{fontSize:11,color:'var(--si)',marginTop:2}}>{nbAttente} controle{nbAttente>1?'s':''} non traite{nbAttente>1?'s':''}. Decidez ce soir : reporter ou abandonner.</div>
        </div>
        <span style={{fontSize:11,color:'var(--si)',fontWeight:600}}>{new Date().getHours()}h{String(new Date().getMinutes()).padStart(2,'0')}</span>
      </div>}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div className="D" style={{fontSize:18}}>Controles du jour</div>
        <div style={{fontSize:11,color:'var(--si)'}}>{new Date().toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'})}</div>
      </div>

      {nbAttente===0&&faits.length===0&&<div className="empty">Aucun controle a traiter aujourd'hui. Ils apparaissent apres chaque rapport soumis, ou via l'onglet Controle direct.</div>}

      {nbAttente>0&&<div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:20}}>
        {enAttente.map(r=>{
          const bien=biens.find(b=>b.id===r.bid);
          const ctrl=controles.find(c=>c.repId===r.id);
          const isReporte=ctrl?.verdict==='reporter';
          const lastH=ctrl?.history?.slice(-1)[0];
          return(<div key={r.id} className="card" style={{padding:'13px 16px',borderLeft:`3px solid ${isReporte?'var(--ye)':'var(--or)'}`}}>
            <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:10,alignItems:'flex-start'}}>
              <div>
                <div style={{display:'flex',gap:7,alignItems:'center',marginBottom:4,flexWrap:'wrap'}}>
                  <span style={{fontWeight:700,fontSize:14}}>{bien?.name||'—'}</span>
                  {isReporte&&<span style={{background:'#fff9e0',color:'#7a5a00',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>📅 Reporte</span>}
                </div>
                <div style={{fontSize:11,color:'var(--si)'}}>Menage soumis le {fd(r.sub||r.date)}</div>
                {(()=>{const da=(dureeAnom||[]).find(a=>a.tid===r.tid&&!a.traite);if(!da)return null;const senslbl=da.sens==='sous'?'trop rapide':'trop long';return<div style={{marginTop:4,background:'#fff4d6',border:'1.5px solid var(--or)',borderRadius:8,padding:'5px 10px',fontSize:11}}><span style={{fontWeight:700,color:'#7a5a00'}}>⏱️ Seuil qualite</span> <span style={{color:'var(--ink)'}}>{senslbl} · {da.motif}</span></div>;})()}
                {isReporte&&lastH&&<div style={{fontSize:10,color:'#7a5a00',marginTop:3}}>Reporte le {ft(lastH.at)} par {lastH.by}</div>}
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',flexShrink:0}}>
                <button onClick={()=>setEditCtrl({repId:r.id,bid:r.bid,existing:ctrl||null})} className="btn bor sm">
                  🔍 {isReporte?'Controler':'Demarrer'}
                </button>
                <button onClick={()=>actionRapide(r.id,r.bid,'reporter')} disabled={!!rapideBusy} className="btn bgh sm" style={{color:'#7a5a00',borderColor:'var(--ye)',opacity:rapideBusy&&rapideBusy!==`${r.id}|reporter`?.45:1}}>
                  {rapideBusy===`${r.id}|reporter`?'⏳ Enregistrement…':'📅 Reporter'}
                </button>
                <button onClick={()=>actionRapide(r.id,r.bid,'abandonne')} disabled={!!rapideBusy} className="btn bgh sm" style={{color:'var(--si)',opacity:rapideBusy&&rapideBusy!==`${r.id}|abandonne`?.45:1}}>
                  {rapideBusy===`${r.id}|abandonne`?'⏳ Enregistrement…':'🗑 Abandonner'}
                </button>
              </div>
            </div>
          </div>);
        })}
      </div>}

      {faits.length>0&&<div>
        <div className="D" style={{fontSize:16,marginBottom:10}}>✅ Controles recents (7 jours)</div>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {faits.map(c=><CarteCtrl key={c.id} c={c}/>)}
        </div>
      </div>}
    </div>}

    {/* ONG. CONTROLE DIRECT */}
    {tabCtrl==='direct'&&<div>
      <div className="D" style={{fontSize:18,marginBottom:6}}>➕ Controle direct</div>
      <p style={{fontSize:13,color:'var(--si)',marginBottom:16}}>Lancez un controle sur n'importe quel appartement, meme sans rapport de menage. Utile pour les controles inopines ou les verifications avant arrivee.</p>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end',marginBottom:8}}>
        <div style={{flex:'1 1 200px'}}>
          <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Appartement</label>
          <select value={directBid} onChange={e=>setDirectBid(e.target.value)} className="inp" style={{width:'100%'}}>
            <option value="">-- Choisir --</option>
            {(biens||[]).filter(b=>!b.bienAnnexe).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <button
          onClick={()=>{ if(directBid) setEditCtrl({repId:null,bid:directBid,existing:null}); }}
          disabled={!directBid}
          className="btn bmn"
        >
          🔍 Demarrer le controle →
        </button>
      </div>
      <div style={{fontSize:11,color:'var(--si)',background:'var(--bg)',borderRadius:8,padding:'8px 12px'}}>
        Le controle sera enregistre sans lien a un rapport de menage. Il apparaitra dans les Archives et sera pris en compte dans le statut de l'appartement.
      </div>
    </div>}

    {/* ONG. ARCHIVES */}
    {tabCtrl==='archives'&&<div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <div className="D" style={{fontSize:18}}>🗂 Archives des controles</div>
        <div style={{fontSize:11,color:'var(--si)'}}>{archives.length} resultat{archives.length>1?'s':''}</div>
      </div>

      {/* Filtres */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16,padding:'10px 12px',background:'var(--bg)',borderRadius:10}}>
        <select value={archBien} onChange={e=>setArchBien(e.target.value)} className="inp" style={{fontSize:12,flex:'1 1 140px',minWidth:0}}>
          <option value="">Tous les appartements</option>
          {(biens||[]).filter(b=>!b.bienAnnexe).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={archVerdict} onChange={e=>setArchVerdict(e.target.value)} className="inp" style={{fontSize:12,flex:'0 0 130px'}}>
          <option value="all">Tous les verdicts</option>
          <option value="pret">✅ Pret</option>
          <option value="nonpret">⚠️ Non pret</option>
        </select>
        <input type="date" value={archDateDeb} onChange={e=>setArchDateDeb(e.target.value)} className="inp" style={{fontSize:12,flex:'0 0 130px'}} title="Date de debut"/>
        <input type="date" value={archDateFin} onChange={e=>setArchDateFin(e.target.value)} className="inp" style={{fontSize:12,flex:'0 0 130px'}} title="Date de fin"/>
        {(archBien||archVerdict!=='all'||archDateDeb||archDateFin)&&<button onClick={()=>{setArchBien('');setArchVerdict('all');setArchDateDeb('');setArchDateFin('');}} className="btn bgh sm" style={{alignSelf:'center'}}>✕ Reinitialiser</button>}
      </div>

      {archives.length===0
        ?<div className="empty">Aucun controle dans les archives{(archBien||archVerdict!=='all'||archDateDeb||archDateFin)?' pour ces criteres':''}.</div>
        :<div style={{display:'flex',flexDirection:'column',gap:8}}>
          {archives.map(c=><CarteCtrl key={c.id} c={c}/>)}
        </div>
      }
    </div>}

    {editCtrl!==null&&<M onC={closeCtrl} title="🔍 Controle appartement" col="var(--mn)" wide ch={<CtrlModal key={editCtrl.existing?.id||editCtrl.repId||editCtrl.bid} repId={editCtrl.repId} bien={biens.find(b=>b.id===editCtrl.bid)} controleurs={gouvernantes} existing={editCtrl.existing} onSave={saveCtrl} onClose={closeCtrl} onDirtyChange={setCtrlDirty} sess={sess}/>}/>}
  </div>);
}


const VAPID_PUBLIC_KEY='BDPnpHbHSYqqy-6BDbG-ezRrRINiHOyU2Rru9V9PbbCWDJ1BLy6D7sx3-QZFxBFsquTkgYptgwveLkpeNLaSQls';

// Helper : convertit une clé VAPID base64-URL en Uint8Array (requis par pushManager.subscribe)
function urlBase64ToUint8Array(base64String){
  const padding='='.repeat((4-base64String.length%4)%4);
  const base64=(base64String+padding).replace(/-/g,'+').replace(/_/g,'/');
  const rawData=window.atob(base64);
  const outputArray=new Uint8Array(rawData.length);
  for(let i=0;i<rawData.length;++i)outputArray[i]=rawData.charCodeAt(i);
  return outputArray;
}


// Fonctionne pour n'importe quel PMS exposant un export iCal (Eviivo, Beds24, ...) :
// le proxy CORS (Edge Function sync-eviivo-ical) valide lui-même le domaine de chaque URL,
// cette fonction ne fait que transmettre et n'a rien de spécifique à un vendeur.
async function fetchPmsIcal(sources){
  // sources = [{bid, url}]  → retourne {results:[{bid, ok, events:[{uid,summary,start,end}], error}]}
  const r=await fetch(`${SUPA_URL}/functions/v1/sync-eviivo-ical-`,{
    method:'POST',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':'application/json'},
    body:JSON.stringify({sources})
  });
  if(!r.ok){
    const txt=await r.text().catch(()=>'');
    throw new Error('Edge sync-eviivo-ical HTTP '+r.status+(txt?' — '+txt.slice(0,200):''));
  }
  return r.json();
}
// Alias rétrocompatible (ancien nom, même comportement).
const fetchEviivoIcal=fetchPmsIcal;

function buildEviivoTasks(bien,ev,vendor){
  const bid=bien.id;
  const dmMenage=Number(bien.dmFull||0)||90; // durée ménage complet par défaut du bien, sinon 90
  // ev = {uid,start,end}. start=arrivée, end=départ (=jour ménage, convention exclusive)
  const mk=(type,date,dm)=>({
    id:'t'+Date.now()+Math.random().toString(36).slice(2,7),
    sid:null,bid,type,date,
    sh:EVIIVO_HEURES[type].sh,sm:EVIIVO_HEURES[type].sm,
    dm:dm||60,notes:'',st:'todo',prio:'normal',
    rs:null,re:null,surcharge25:false,risque:false,applyTarifUrgent:false,noReport:false,
    source:'eviivo',eviivoUid:ev.uid,pmsVendor:vendor||'eviivo'
  });
  return[
    mk('checkin', ev.start, 30),
    mk('checkout',ev.end,   30),
    mk('full',    ev.end,   dmMenage),
  ];
}

// Réconcilie un lot de résultats iCal — Eviivo et/ou Beds24, potentiellement mélangés dans le
// même appel — avec l'état courant (séjours + tâches staff). Le vendeur (pmsVendor) de chaque
// bien est déduit de biens[].icalUrl, pas besoin de le passer en paramètre : un même clic
// "Synchroniser" peut couvrir des biens encore sur Eviivo et d'autres déjà basculés Beds24
// (migration bien par bien, v161+).
// NE TOUCHE JAMAIS aux séjours/tâches 'manuel'. Append-only : aucune suppression.
// Retourne {sejours, st, stats} prêts à être persistés, + liste d'events notif à émettre.
function reconcilePms(results,curSejours,curSt,biens){
  let sejours=[...curSejours];
  let st=[...curSt];
  const notifs=[];
  const stats={crees:0,majDates:0,annulesDetectes:0,inchanges:0};

  const vendorByBid={};
  (biens||[]).forEach(b=>{vendorByBid[b.id]=detectPmsVendor(b.icalUrl)||'eviivo';});

  // UID encore présents dans l'iCal (par bien) → pour détecter les disparitions
  const presentByBid={};

  (results||[]).forEach(res=>{
    if(!res.ok)return; // bien en erreur réseau : on ne touche RIEN pour ce bien (prudence)
    const bid=res.bid;
    const vendor=vendorByBid[bid]||'eviivo';
    presentByBid[bid]=new Set();

    (res.events||[]).forEach(ev=>{
      if(!ev.uid||!ev.start||!ev.end)return;

      // ── STATUS:CANCELLED : le PMS garde les résa annulées dans l'iCal avec ce flag.
      // On NE les ajoute PAS à presentByBid (leur UID sera donc "absent" pour la détection
      // par disparition) ET on déclenche immédiatement l'annulation si le séjour existe
      // (scopé au vendeur courant du bien : cf. note sur la détection d'annulation plus bas).
      if(ev.cancelled){
        const cIdx=sejours.findIndex(s=>s.source==='eviivo'&&s.eviivoUid===ev.uid&&s.bid===bid&&(s.pmsVendor||'eviivo')===vendor);
        if(cIdx!==-1){
          const sc=sejours[cIdx];
          if(sc.statut!=='annule_eviivo'&&sc.statut!=='annulee'&&sc.statut!=='ignore'){
            stats.annulesDetectes++;
            notifs.push({type:'sejour_a_verifier',bid,date:sc.dateArrivee,uid:ev.uid,raison:'annulation'});
            sejours[cIdx]={...sc,statut:'annule_eviivo'};
          }
        }
        return; // ne pas traiter cet event comme une résa active
      }

      presentByBid[bid].add(ev.uid);

      // Séjour déjà connu avec cet UID, pour ce bien ET ce vendeur ?
      const exIdx=sejours.findIndex(s=>s.source==='eviivo'&&s.eviivoUid===ev.uid&&s.bid===bid&&(s.pmsVendor||'eviivo')===vendor);
      if(exIdx===-1){
        // ── Pas de séjour avec cet UID pour ce vendeur. Avant de créer, deux vérifications :

        // 1) Un séjour DÉJÀ SAISI À LA MAIN (sans eviivoUid) sur le même bien + mêmes dates.
        //    Si trouvé → on le RATTACHE (on lui pose l'eviivoUid + le vendeur) au lieu de
        //    dupliquer. On ne touche ni à son statut, ni à ses montants, ni à ses tâches.
        const orphanIdx=sejours.findIndex(s=>
          !s.eviivoUid && s.bid===bid &&
          s.dateArrivee===ev.start && s.dateDepart===ev.end &&
          s.statut!=='annulee'
        );
        if(orphanIdx!==-1){
          const s=sejours[orphanIdx];
          sejours[orphanIdx]={...s,source:'eviivo',eviivoUid:ev.uid,pmsVendor:vendor,refEviivo:s.refEviivo||ev.summary||''};
          stats.rattaches=(stats.rattaches||0)+1;
          // pas de nouvelles tâches, pas de notif : ce séjour existait déjà chez toi.
        }else{
          // 2) MIGRATION PMS : un séjour existe déjà sur ce bien + mêmes dates, importé par
          //    un AUTRE vendeur (cas d'un bien tout juste basculé Eviivo→Beds24 : Eviivo avait
          //    déjà posé un eviivoUid dessus). On le RÉ-ANCRE sur le nouvel UID/vendeur au lieu
          //    de le dupliquer — statut, montants, tâches déjà faites restent intacts, seuls
          //    l'UID et le vendeur changent, et les tâches liées suivent le nouvel UID (sinon
          //    une annulation future côté Beds24 ne retrouverait plus ses tâches à repasser
          //    'à vérifier').
          const crossIdx=sejours.findIndex(s=>
            s.source==='eviivo' && s.eviivoUid && (s.pmsVendor||'eviivo')!==vendor &&
            s.bid===bid && s.dateArrivee===ev.start && s.dateDepart===ev.end &&
            s.statut!=='annulee' && s.statut!=='ignore'
          );
          if(crossIdx!==-1){
            const s=sejours[crossIdx];
            const oldUid=s.eviivoUid;
            sejours[crossIdx]={...s,eviivoUid:ev.uid,pmsVendor:vendor,
              statut:s.statut==='annule_eviivo'?'a_completer':s.statut,
              refEviivo:s.refEviivo||ev.summary||''};
            st=st.map(t=>(t.source==='eviivo'&&t.eviivoUid===oldUid)?{...t,eviivoUid:ev.uid,pmsVendor:vendor}:t);
            stats.migres=(stats.migres||0)+1;
          }else{
            // 3) VRAI nouveau séjour
            const nbNuits=Math.max(1,Math.round((new Date(ev.end)-new Date(ev.start))/86400000));
            const bienObj=(biens||[]).find(b=>b.id===bid)||{id:bid};
            sejours.push({
              ...SEJOUR_EMPTY,
              id:'sej'+Date.now()+Math.random().toString(36).slice(2,6),
              // cid hérité du bien dès la création : sans lui le séjour est invisible
              // dans l'Aide à la facturation (filtre sur s.cid) alors qu'il reste visible
              // côté propriétaire (filtre sur bien.cid). Voir HANDOFF v131.
              bid,cid:bienObj.cid||'',dateArrivee:ev.start,dateDepart:ev.end,nuitees:nbNuits,
              source:'eviivo',eviivoUid:ev.uid,pmsVendor:vendor,statut:'a_completer',
              refEviivo:ev.summary||''
            });
            // 3 tâches non affectées
            buildEviivoTasks(bienObj,ev,vendor).forEach(t=>st.push(t));
            stats.crees++;
            notifs.push({type:'sejour_importe',bid,date:ev.start,uid:ev.uid,vendor});
          }
        }
      }else{
        // ── séjour connu pour ce vendeur : dates changées ? ──
        const s=sejours[exIdx];
        // Un séjour explicitement écarté par l'admin (ignore = doublon, annulee = annulation confirmée)
        // ne doit JAMAIS être réactivé par une synchro. On le laisse tel quel.
        if(s.statut==='ignore'||s.statut==='annulee'){
          stats.inchanges++;
        }else{
        const datesChg=s.dateArrivee!==ev.start||s.dateDepart!==ev.end;
        // si annulé précédemment puis réapparu : on le réactive en 'a_completer'
        const etaitAnnule=s.statut==='annule_eviivo';
        if(datesChg||etaitAnnule){
          sejours[exIdx]={...s,dateArrivee:ev.start,dateDepart:ev.end,
            statut:s.statut==='annule_eviivo'?'a_completer':s.statut,
            _eviivoDatesMaj:datesChg?true:s._eviivoDatesMaj};
          // tâches liées repassées en 'todo' + dates recalées si besoin
          st=st.map(t=>{
            if(t.source!=='eviivo'||t.eviivoUid!==ev.uid)return t;
            const newDate=t.type==='checkin'?ev.start:ev.end;
            return{...t,date:newDate,st:(t.st==='a_verifier'?'todo':t.st)};
          });
          stats.majDates++;
          notifs.push({type:'sejour_a_verifier',bid,date:ev.start,uid:ev.uid,raison:'dates'});
        }else{
          stats.inchanges++;
        }
        }
      }
    });
  });

  // ── Détection des ANNULATIONS : UID connu absent de l'iCal du bien ──
  // (uniquement pour les biens effectivement synchronisés avec succès)
  // SCOPÉ AU VENDEUR COURANT DU BIEN (vendorByBid) : indispensable pour la migration — sans ce
  // filtre, un séjour historique Eviivo se ferait marquer "annulé" dès que son bien bascule sur
  // Beds24, puisque son eviivoUid n'apparaît plus jamais dans le flux Beds24 qu'on interroge
  // désormais. Les séjours d'un vendeur qu'on ne synchronise plus pour ce bien restent gelés,
  // comme n'importe quelle archive.
  // Couvre tous les statuts eviivo sauf : ignore (doublon écarté), annulee (déjà confirmé),
  // annule_eviivo (déjà marqué en attente de confirmation).
  sejours=sejours.map(s=>{
    if(s.source!=='eviivo')return s;
    if(!s.eviivoUid)return s;                   // séjour sans UID (ne devrait pas arriver) → prudence
    const present=presentByBid[s.bid];
    if(!present)return s;                       // bien non sync ce tour-ci → on ne juge pas
    if((s.pmsVendor||'eviivo')!==(vendorByBid[s.bid]||'eviivo'))return s; // vendeur du séjour ≠ vendeur actif du bien → archive gelée
    if(s.statut==='annule_eviivo')return s;     // déjà marqué, en attente de confirmation admin
    if(s.statut==='ignore'||s.statut==='annulee')return s; // écarté par l'admin → ne jamais re-signaler
    if(present.has(s.eviivoUid))return s;       // UID toujours présent dans le flux → inchangé
    // UID disparu du flux iCal → annulation détectée (couvre a_completer ET draft rattachés)
    stats.annulesDetectes++;
    notifs.push({type:'sejour_a_verifier',bid:s.bid,date:s.dateArrivee,uid:s.eviivoUid,raison:'annulation'});
    return{...s,statut:'annule_eviivo'};
  });
  // tâches des séjours annulés → 'a_verifier' (JAMAIS supprimées)
  const uidsAnnules=new Set(sejours.filter(s=>s.source==='eviivo'&&s.statut==='annule_eviivo').map(s=>s.eviivoUid));
  st=st.map(t=>{
    if(t.source!=='eviivo'||!uidsAnnules.has(t.eviivoUid))return t;
    if(t.st==='done')return t;                  // déjà fait : on n'y touche pas
    return{...t,st:'a_verifier'};
  });

  return{sejours,st,notifs,stats};
}
// Alias rétrocompatible (ancien nom, même comportement : vendeur déduit de biens[].icalUrl).
const reconcileEviivo=reconcilePms;

export { NoteEtoiles, CtrlModal, SecRapports, SecControles, urlBase64ToUint8Array, buildEviivoTasks, reconcilePms, reconcileEviivo, fetchPmsIcal, fetchEviivoIcal };
